import React from "react";
import Title from "../../ui/Title";

const IndiaHealth = () => {
  return (
    <div>
      <Title title="How countries of the Nooraverse fare in mental healthcare and support" />

      <div
        id="india"
        className="space-y-4 text-softBlack leading-loose text-base font-normal scroll-mt-24"
      >
        <p className="font-bold text-xl text-black mb-4 !mt-7">India</p>

        <ul className="space-y-4 list-disc list-outside ml-6">
          <li>
            <a
              href="https://mohfw.gov.in/sites/default/files/National%20Mental%20Health%20Survey%2C%202015-16%20-%20Mental%20Health%20Systems_0.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              Data
            </a>{" "}
            from the National Mental Health Survey 2015-16 suggests that
            approximately 15 percent of Indian adults (aged 18 and above)
            require active interventions for one or more mental health issues.
          </li>
          <li>
            The survey also reveals a treatment gap ranging from 70 to 92
            percent across different conditions. The treatment gap refers to the
            number of people who need treatment but do not receive it.
          </li>
          <li>
            India spends less than 1 percent of its total health budget on
            mental health.
          </li>
          <li>
            According to the WHO, there are only{" "}
            <a
              href="https://www.who.int/data/gho/data/indicators/indicator-details/GHO/psychiatrists-working-in-mental-health-sector-(per-100-000)"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              0.292 psychiatrists
            </a>{" "}
            and{" "}
            <a
              href="https://www.who.int/data/gho/data/indicators/indicator-details/GHO/psychologists-working-in-mental-health-sector-(per-100-000)"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              0.069 psychologists
            </a>{" "}
            per 100,000 population.
          </li>
        </ul>

        <p className="font-bold text-black">
          Mental health policy and Mental Healthcare Act
        </p>

        <div className="space-y-4">
          <p>
            India has a national-level mental health policy{" "}
            <a
              href="https://nhm.gov.in/images/pdf/National_Health_Mental_Policy.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              (National Mental Health Policy, 2014)
            </a>{" "}
            which consists of strategies and recommendations to improve mental
            healthcare in the country. India also has a mental health law
            (Mental Healthcare Act, 2017), which regulates mental healthcare and
            treatment and protects the rights of persons with mental illnesses.
          </p>

          <p>
            According to a 2021{" "}
            <a
              href="https://journals.sagepub.com/doi/10.1177/02537176211048335"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              research paper
            </a>{" "}
            analyzing India’s national mental health policy, its main focus is
            the promotion of mental health, prevention of mental illnesses,
            de-stigmatization, and ensuring socio-economic inclusion of persons
            with mental illness through accessible, affordable, and quality
            healthcare and social services within a rights-based framework. The
            policy intends on the delivery of mental health services through an
            integrated care model.
          </p>
          <p>
            Some limitations of the policy highlighted in the analysis include:
          </p>
          <ul className="space-y-2 list-disc list-outside ml-6">
            <li>
              Training of general practitioners at the community level for
              mental health remains very limited
            </li>
            <li>
              Coordination between different departments is weak, and there is
              no functional central or national-level coordination committee to
              address the issues
            </li>
            <li>
              There are insufficient mechanisms to ensure the regular flow of
              funds
            </li>
          </ul>
          <p>
            <a
              href="https://www.indiacode.nic.in/bitstream/123456789/2249/1/A2017-10.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              The Mental Healthcare Act 2017
            </a>
            , on the other hand, regulates mental healthcare and treatment in
            India. It replaces the previous legislation — the Mental Health Act
            of 1987. According to a{" "}
            <a
              href="https://cmhlp.org/wp-content/uploads/2022/05/Visual-Summary-Resource-on-Indias-Mental-Health-for-Youth-Participants.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              summary
            </a>{" "}
            by the Centre for Mental Health Law and Policy, the new act adopts a
            rights-based approach to mental healthcare and treatment, and it
            places obligations on governments and mental health professionals to
            protect the rights of persons with mental illness. The law outlines
            regulations for determining mental illness, admission to treatment
            facilities, autonomy and rights of persons with mental illness,
            confidentiality, and the role of various authorities like police and
            magistrates.
          </p>
        </div>

        <p className="font-bold text-black">
          Tele-MANAS (Tele Mental Health Assistance and Networking Across
          States)
        </p>

        <div className="space-y-4">
          <p>
            The Government of India launched the{" "}
            <a
              href="https://telemanas.mohfw.gov.in/home"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              National Tele Mental Health Program (NTMHP)
            </a>{" "}
            in 2022. Tele-MANAS was introduced as a toll-free helpline from
            which users can avail a range of mental health services. The program
            aims to improve access and reduce barriers related to the cost of
            mental healthcare. Tele-MANAS lets users use telephone-based
            counseling, psychotherapy, psychiatric consultations, and referral
            services including urgent care.
          </p>

          <div className="flex flex-col items-center justify-center my-6">
            <img
              src="/assets/img/tele-manas-health.png"
              alt="Tele-MANAS: How it works"
              className=""
            />
          </div>

          <p className="text-center italic">
            Source:{" "}
            <a
              href="https://telemanas.mohfw.gov.in/about"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              TeleManas
            </a>
            , Ministry of Health and Family Welfare
          </p>
          <p>
            When a user calls the toll-free number (IVRS), the caller will be
            attended to by a trained counselor. The counselor (trained and
            accredited) will provide the required care if the needs are within
            their capabilities. When the user needs urgent in-person
            intervention or complex evaluations and management, the caller will
            be referred to a consultation with a specialist through eSanjeevani,
            or referred to the nearest in-person service — ranging from Health
            and Wellness Centers (HWCs) to tertiary centers which are part of
            the District Mental Health Program.
          </p>
        </div>

        <p className="font-bold text-black">District Mental Health Program</p>

        <div className="space-y-4">
          <p>
            The District Mental Health Program (DMHP) is the flagship program of
            the{" "}
            <a
              href="https://nhm.gov.in/index1.php?lang=1&level=2&sublinkid=1043&lid=359"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              National Mental Health Programme (NMHP)
            </a>
            . The program aims to decentralize mental health services and make
            them available at the community level by integrating mental health
            into the general healthcare system and improving community
            participation. The NMHP was launched in 1982 and it was one of the{" "}
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6102961/#:~:text=The%20Bellary%20model%20demonstrated%20that,at%20the%20primary%20health%20centers."
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              first programs of its kind
            </a>{" "}
            in low and middle income countries.
          </p>
          <p>
            According to the{" "}
            <a
              href="https://cmhlp.org/wp-content/uploads/2021/11/Issue-Brief-DMHP-II.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              article series
            </a>{" "}
            Deconstructing DHMP by the Centre for Mental Health Law & Policy,
            the program “focuses more on a biomedical model rather than a
            community model.” One of the{" "}
            <a
              href="https://cmhlp.org/wp-content/uploads/2021/11/Issue-Brief-DMHP-IV.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              issue briefs
            </a>{" "}
            states that there is more “emphasis on the psychiatric approach with
            a focus on the distribution of psychotropic medication with limited
            emphasis on social, cultural, and family dynamics”, which are key
            areas to address for effective mental health interventions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IndiaHealth;
